import { BankOutlined, DownOutlined } from "@ant-design/icons"
import { Button, Dropdown, type MenuProps } from "antd"
import React, { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { ALL_BRANCHES } from "../redux/branches.constants.ts"
import {
  fetchAccessibleBranches,
  selectBranchesState,
  setGlobalBranch
} from "../redux/branches.slice.ts"

// Company-level pages that exist in both Company HQ and a specific
// branch — switching between them keeps the current page rather than
// bouncing to Dashboard.
const SHARED_PAGE_PATHS = ["/home", "/reports", "/invoices", "/calendar", "/team_management"]

// HQ-only management screens with no branch-workspace equivalent — only
// Company HQ actually administers every branch (create/archive/restore,
// see `apps.user.branch_service`). Leaving one of these on screen under a
// specific branch would show company-wide controls that branch's own
// authority never covers. Landing here directly while a branch is active
// is handled separately by each route's own guard (see
// `BranchesOwnerGate`/`OpenInCompanyHQPrompt`); this list only covers the
// case of SWITCHING into a branch while already on one of these pages.
const COMPANY_ONLY_PAGE_PATHS = [RoutesPaths.settings.branches]

// "Company HQ" is a management workspace, never an operational branch —
// this label is display-only, layered over the same `ALL_BRANCHES`
// sentinel value everywhere else in the app (no dummy Team/Branch row).
const COMPANY_HQ_LABEL = "Company HQ"

/**
 * The ONE global workspace selector — where the owner is WORKING, distinct
 * from each page's own "Branches" filter (which data they're VIEWING; see
 * `PageBranchFilter`). Never itself an authorization decision — every
 * server endpoint underneath independently re-validates the caller's real
 * accessible teams (`apps.user.branch_scope`) regardless of what this
 * shows.
 *
 * Single-select, always: "Company HQ" (owner only) plus every accessible
 * active branch. Picking a specific branch immediately shows the full
 * existing app scoped to it — there is no separate "open workspace" step,
 * the selector's value IS the mode. A single-branch account never sees
 * this at all — "users with one branch open it automatically and do not
 * need a selector."
 */
export const BranchSelector = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isOwner, accessibleBranches, globalBranch, gcc, fetching } =
    useAppSelector(selectBranchesState)

  useEffect(() => {
    dispatch(fetchAccessibleBranches())
  }, [dispatch])

  if (fetching && accessibleBranches.length === 0) return null
  if (!gcc?.multi_branch_enabled || accessibleBranches.length <= 1) return null

  const currentLabel =
    globalBranch === ALL_BRANCHES
      ? COMPANY_HQ_LABEL
      : accessibleBranches.find((b) => b.id === globalBranch)?.name || "Select branch"

  const items: MenuProps["items"] = [
    ...(isOwner
      ? [{ key: ALL_BRANCHES, label: COMPANY_HQ_LABEL }, { type: "divider" as const }]
      : []),
    ...accessibleBranches.map((branch) => ({
      key: branch.id,
      label: branch.name
    }))
  ]

  const onClick: MenuProps["onClick"] = ({ key }) => {
    dispatch(setGlobalBranch(key))
    // "Preserve the current page when it exists in both views... When
    // switching from a branch-only page to Company HQ, navigate to the
    // company Dashboard."
    if (key === ALL_BRANCHES && !SHARED_PAGE_PATHS.includes(location.pathname)) {
      navigate(RoutesPaths.home)
      return
    }
    // The reverse: leaving an HQ-only management screen (Branches) for a
    // specific branch — that screen has no branch-workspace equivalent,
    // so staying on it would show company-wide controls under a branch's
    // own authority. Lands on that branch's own settings instead, same
    // page the "Open in Company HQ" prompt's own CTA would have sent them
    // back from.
    if (
      key !== ALL_BRANCHES &&
      COMPANY_ONLY_PAGE_PATHS.some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
      )
    ) {
      navigate(RoutesPaths.settings.company)
    }
  }

  return (
    // Explicit `trigger: ["click"]` — AntD's `Dropdown` defaults to
    // `["hover"]` (inherited from rc-dropdown), which never opens from a
    // native Enter/Space activation on the focused trigger button (only a
    // real pointer click does), making this control entirely
    // keyboard-unreachable. `click` is itself what Enter/Space on a
    // focused `<button>` fires, so this is the minimal change that makes
    // the menu openable by keyboard at all.
    <Dropdown menu={{ items, onClick, selectedKeys: [globalBranch] }} trigger={["click"]}>
      <Button
        icon={<BankOutlined />}
        data-testid="branch-selector"
        data-guide-target="workspace-selector"
      >
        {currentLabel}
        <DownOutlined className="text-xs" />
      </Button>
    </Dropdown>
  )
}

export default BranchSelector
